import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';
import { useGlossary, lookupTerm } from '@/hooks/useGlossary';
import { tipKeyAction } from '@/lib/glossaryTipKeys';

/* SPEC-453 — "what does this actually mean?", answered in place.
 *
 * One ⓘ affordance, used on freshness-ribbon source labels, headline figure titles and
 * honest-state pills. The meaning comes from the ONE glossary the copilot reads
 * (backend/agent_glossary.py, served by GET /api/governance/glossary) — this component
 * defines no copy of its own, so the screen and the copilot cannot answer differently.
 *
 * HONESTY RULES
 *  - A term the glossary does not define renders NOTHING. A ⓘ that opens to say "no
 *    definition" is worse than no ⓘ: it advertises an answer it does not have.
 *  - `detail` is the SPECIFIC cause the engine recorded for THIS row. It is shown under the
 *    general meaning and is never invented — callers pass only a recorded value, and the
 *    pill says "no further detail recorded" when there is none.
 *
 * A11Y: hover AND keyboard focus open it (a hover-only affordance is unreachable by
 * keyboard). The trigger is a real <button> carrying an accessible name, and the panel is
 * `role="tooltip"` wired by `aria-describedby`, because it holds text only — no links, no
 * controls (the interactive-popover case on DemandPlanningPage deliberately is not a
 * tooltip). Colours are tokens, never alpha-on-text, so the contrast guard stays quiet.
 * KEYBOARD (round 7): the panel lives in a portal at the end of <body>, so the browser's own Tab
 * would leave the ⓘ for the next page control and close the panel before "more" was reachable.
 * Tab / Shift+Tab / Escape follow `lib/glossaryTipKeys` instead: ⓘ → "more" → the control after
 * the ⓘ (closing), Shift+Tab back to the ⓘ, Escape closes and returns focus to the ⓘ.
 */

const TABBABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), '
  + 'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Focus the first control after `from` in page order, outside `skip` (the panel). */
function focusNextAfter(from: HTMLElement, skip: HTMLElement | null): void {
  for (const el of Array.from(document.querySelectorAll<HTMLElement>(TABBABLE))) {
    if (el === from || from.contains(el) || skip?.contains(el) || el.tabIndex < 0) continue;
    if (!(from.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING)) continue;
    el.focus();
    if (document.activeElement === el) return;   // a hidden control cannot take focus: try on
  }
}

interface Props {
  /** Glossary key: a term name, an alias, or an exact state token (e.g. INSUFFICIENT_DATA). */
  term: string;
  /** The specific, RECORDED cause for this row. Rendered below the general meaning. */
  detail?: string | null;
  /** Shown when the caller has a pill/figure whose cause the payload did not carry. */
  noDetailNote?: string;
  /** Where the panel opens from. Default 'right' keeps it clear of a left page edge. */
  align?: 'left' | 'right' | 'center';
  className?: string;
  /** Accessible name for the trigger. Defaults to naming the term. */
  label?: string;
}

/* SPEC-580 addendum (2) — ONE explainer open at a time. Opening a panel closes whichever other
 * panel is open, so two explainers never stack over each other. */
let closeOpenTip: (() => void) | null = null;

/** A definition longer than this collapses to two lines with "more". */
const LONG_DEFINITION = 140;
/** The panel keeps this far inside the window edges (px). */
const EDGE = 8;

export function GlossaryTip({
  term, detail, noDetailNote, align = 'right', className = '', label,
}: Props) {
  const index = useGlossary();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // Where the panel sits on screen (fixed), worked out after it renders; null until measured.
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const panelId = useId();
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  const panelRef = useRef<HTMLSpanElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const moreRef = useRef<HTMLButtonElement | null>(null);
  // Set while focus is put back on the ⓘ after Escape, so that focus does not reopen the panel.
  const quietFocus = useRef(false);
  const entry = lookupTerm(index, term);

  // A stable closer per panel, so the registry can tell this panel from another one.
  const closer = useRef(() => { setOpen(false); setExpanded(false); setPos(null); }).current;
  const inside = (n: Node | null) => Boolean(n && (wrapRef.current?.contains(n) || panelRef.current?.contains(n)));
  const show = () => {
    if (closeOpenTip && closeOpenTip !== closer) closeOpenTip();
    closeOpenTip = closer;
    setOpen(true);
  };
  const hide = closer;
  useEffect(() => () => { if (closeOpenTip === closer) closeOpenTip = null; }, [closer]);

  // SPEC-580 addendum (2): the panel is drawn in a portal at a FIXED place on screen, so no
  // scrolling table or card around the ⓘ can clip it (the method table's own sideways scroll cut
  // it in half). It sits under the trigger, or above it when there is no room below, and is
  // shifted to stay inside the window. Measured after it renders; re-measured on "more".
  useLayoutEffect(() => {
    if (!open || !panelRef.current || !wrapRef.current) return;
    const r = panelRef.current.getBoundingClientRect();
    const t = wrapRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const want = align === 'left' ? t.left : align === 'center' ? t.left + t.width / 2 - r.width / 2 : t.right - r.width;
    const left = Math.max(EDGE, Math.min(want, vw - EDGE - r.width));
    const below = t.bottom + r.height <= vh - EDGE || t.top - r.height < EDGE;
    const top = below ? t.bottom : t.top - r.height;
    if (!pos || pos.top !== top || pos.left !== left) setPos({ top, left });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, expanded]);

  // A fixed panel would float away from its ⓘ when the page scrolls, so a scroll closes it.
  useEffect(() => {
    if (!open) return undefined;
    // The first frame after opening is ignored: focusing an ⓘ can scroll it into view (review 2).
    const opened = performance.now();
    const onScroll = (e: Event) => {
      if (performance.now() - opened > 100 && !inside(e.target as Node)) closer();
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', closer);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', closer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // No meaning on record → no affordance at all (see HONESTY RULES above).
  if (!entry) return null;
  const long = entry.definition.length > LONG_DEFINITION;

  return (
    <span
      ref={wrapRef}
      className={`relative inline-flex items-center ${className}`}
      onMouseEnter={show}
      // Review (a11y): a pointer leaving does not close a panel whose "more" holds keyboard focus.
      // Only focus INSIDE the panel (its "more") keeps it open; a clicked ⓘ does not (review 2).
      onMouseLeave={() => { if (!panelRef.current?.contains(document.activeElement)) hide(); }}
      // Keys from the ⓘ and from the panel's "more" (React bubbles portal events here).
      onKeyDown={(e) => {
        const at = e.target === moreRef.current ? 'more' : e.target === triggerRef.current ? 'trigger' : null;
        if (!at) return;
        const act = tipKeyAction({ key: e.key, shift: e.shiftKey, at, open, hasMore: long });
        if (act === 'native') return;
        e.preventDefault();
        e.stopPropagation();
        if (act === 'focus-more') moreRef.current?.focus();
        else if (act === 'focus-trigger') triggerRef.current?.focus();
        else if (act === 'close-to-next') {
          if (triggerRef.current) focusNextAfter(triggerRef.current, panelRef.current);
          hide();
        } else if (act === 'close-to-trigger') {
          quietFocus.current = true;
          triggerRef.current?.focus();
          quietFocus.current = false;
          hide();
        } else hide();
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-label={label ?? `What does “${entry.term}” mean?`}
        // `aria-describedby` is the whole contract here, and `aria-expanded` is deliberately
        // ABSENT: it declares a disclosure/combobox widget, and a screen reader would announce
        // this as "collapsed" — a control state, for something that is only a description.
        // Focus opens the panel, so the description is attached by the time it is needed.
        aria-describedby={open ? panelId : undefined}
        className="inline-flex items-center text-[var(--text-tertiary)] hover:text-[var(--text-primary)] focus:text-[var(--text-primary)] focus:outline-none focus-visible:ring-1 focus-visible:ring-[var(--border-default)] rounded"
        onFocus={() => { if (!quietFocus.current) show(); }}
        // Focus moving to the panel's own "more" button keeps it open.
        onBlur={(e) => { if (!inside(e.relatedTarget as Node | null)) hide(); }}
        // OPEN-ONLY, never a toggle. A tap on iOS/Android fires mouseenter → focus → click,
        // so a toggle would open the panel twice and leave it CLOSED — the ⓘ would simply not
        // work on an iPad, which is where the execs read these dashboards. Mouse users got the
        // mirror bug: clicking while already hovering closed it. Leaving (blur / mouseleave)
        // is what closes it, on both input kinds.
        //
        // stopPropagation is REQUIRED, not defensive: these pills render inside `DataTable`
        // rows, whose <tr> carries an onClick that expands the row or navigates away. Without
        // it, asking what a word means would also act on the row — and on the Workbench it
        // would navigate off the figure the reader was asking about.
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); show(); }}
      >
        <Info className="w-3.5 h-3.5" aria-hidden="true" />
      </button>
      {open && createPortal(
        <span
          ref={panelRef}
          id={panelId}
          // A note, not a tooltip: it can hold the "more" button (review: interactive content
          // inside role="tooltip" is not allowed). Still attached by aria-describedby.
          role="note"
          // Same reason as the trigger: a click to select text in the panel must not reach
          // the clickable row underneath it.
          onClick={(e) => e.stopPropagation()}
          style={pos ? { top: pos.top, left: pos.left } : { top: 0, left: 0, visibility: 'hidden' }}
          // Contiguous with the trigger (no top gap) so the pointer never crosses an
          // uncovered strip on its way in — the convention the stock-tile popover set.
          // SPEC-580 addendum (2): at most ~420 px wide, always wrapping (a parent's
          // `whitespace-nowrap` must not run the text off the panel), kept inside the window.
          className="fixed z-[200] w-max max-w-[min(26rem,calc(100vw-1rem))] min-w-[14rem] p-3 rounded-lg border border-[var(--border-default)] bg-[var(--bg-overlay)] shadow-xl text-left normal-case font-normal tracking-normal whitespace-normal break-words"
        >
          <span className="block text-[11px] font-semibold text-[var(--text-primary)]">
            {entry.term}
          </span>
          <span className={`text-[11px] text-[var(--text-secondary)] leading-relaxed mt-1 ${long && !expanded ? 'line-clamp-2' : 'block'}`}>
            {entry.definition}
          </span>
          {long && (
            <button
              ref={moreRef}
              type="button"
              className="text-[11px] text-amber-400 hover:underline mt-0.5"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setExpanded((x) => !x); }}
              onBlur={(e) => { if (!inside(e.relatedTarget as Node | null)) hide(); }}
            >
              {expanded ? 'less' : 'more'}
            </button>
          )}
          {detail ? (
            <span className="block mt-2 pt-2 border-t border-[var(--border-subtle)]">
              <span className="block text-[10px] uppercase text-[var(--text-tertiary)]">
                On this line
              </span>
              <span className="block text-[11px] text-[var(--text-secondary)] leading-relaxed mt-0.5">
                {detail}
              </span>
            </span>
          ) : noDetailNote ? (
            <span className="block mt-2 pt-2 border-t border-[var(--border-subtle)] text-[11px] text-[var(--text-tertiary)] leading-relaxed">
              {noDetailNote}
            </span>
          ) : null}
          {/* The glossary names WHICH open item still governs this term; that id is a
              build-side reference, so the screen states the fact without the token. */}
          {entry.pending_ruling && (
            <span className="block mt-2 pt-2 border-t border-[var(--border-subtle)] text-[10px] text-amber-400 leading-relaxed">
              This is how we run it today. It is still open for you to settle.
            </span>
          )}
        </span>,
        document.body,
      )}
    </span>
  );
}
